import * as THREE from 'three';
import { colors } from '@/utils/colors';
import type { PointerRay } from '@/xr/panelRay';

export type ControllerRaysOptions = {
  /** Length drawn when the ray isn't resting on anything, metres. */
  length?: number;
  color?: THREE.ColorRepresentation;
  /** Beam radius, metres. */
  thickness?: number;
};

type Beam = {
  group: THREE.Group;
  shaft: THREE.Mesh;
  dot: THREE.Mesh;
};

const MAX_CONTROLLERS = 2;

/**
 * Laser pointers for the controllers: what a player holding one points and
 * clicks with, in place of the fingertip poke the panels were first built for.
 *
 * Shown only while there is something to point AT — a panel is up, or the tee is
 * being placed. A permanent laser across the mat would be in the way at address.
 * Poses come straight from `targetRaySpace`, so the ray leaves the controller
 * where the runtime says it points, not where a model guesses.
 */
export class ControllerRays {
  object: THREE.Group;
  /** Live rays this frame, one per tracked controller (hands excluded). */
  rays: PointerRay[] = [];

  #beams: Beam[] = [];
  #length: number;
  /** Parallel to `rays` — which beam each ray came from, so hits can be shown. */
  #rayBeam: number[] = [];
  /** Reused per beam: `rays` hands out references, valid for the current frame. */
  #origins: THREE.Vector3[] = [];
  #directions: THREE.Vector3[] = [];

  constructor(options: ControllerRaysOptions = {}) {
    this.#length = options.length ?? 2.0;
    const color = options.color ?? colors.primary ?? '#21d48d';
    const thickness = options.thickness ?? 0.0022;

    this.object = new THREE.Group();
    this.object.name = 'ControllerRays';
    this.object.userData.mrMaskExempt = true;
    this.object.visible = false;

    for (let i = 0; i < MAX_CONTROLLERS; i++) {
      const group = new THREE.Group();
      group.visible = false;

      // Unit-length cylinder running from the origin along -Z (the target-ray
      // forward), so scale.z alone sets the drawn length.
      const shaftGeometry = new THREE.CylinderGeometry(thickness, thickness, 1, 6, 1, true)
        .rotateX(-Math.PI / 2)
        .translate(0, 0, -0.5);
      const shaft = new THREE.Mesh(shaftGeometry, new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.55,
        depthTest: false,
        depthWrite: false,
        fog: false,
        toneMapped: false,
      }));
      shaft.renderOrder = 44; // over the panels (35/40), under the dot
      shaft.userData.mrMaskExempt = true;

      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.007, 12, 8),
        new THREE.MeshBasicMaterial({
          color,
          depthTest: false,
          depthWrite: false,
          fog: false,
          toneMapped: false,
        })
      );
      dot.renderOrder = 45;
      dot.userData.mrMaskExempt = true;
      dot.visible = false;

      group.add(shaft, dot);
      this.object.add(group);
      this.#beams.push({ group, shaft, dot });
      this.#origins.push(new THREE.Vector3());
      this.#directions.push(new THREE.Vector3(0, 0, -1));
    }
  }

  setVisible(visible: boolean) {
    this.object.visible = visible;
    if (!visible) {
      for (const beam of this.#beams) beam.dot.visible = false;
    }
  }

  /**
   * Re-read the target-ray poses. Pass the input sources from XRShotControls so
   * connect/disconnect plumbing lives in exactly one place.
   *
   * Controllers are gathered by filtering rather than by slot index: with hand
   * tracking also active a session carries up to four input sources, and the
   * controllers can sit at any slot — including past the first two.
   */
  update(
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
    inputSources: readonly (XRInputSource | undefined)[],
  ) {
    this.rays.length = 0;
    this.#rayBeam.length = 0;

    const controllers = inputSources.filter(
      (source): source is XRInputSource => !!source && !(source as any).hand
    );

    for (let i = 0; i < this.#beams.length; i++) {
      const beam = this.#beams[i];
      const source = controllers[i];
      const space = source?.targetRaySpace;
      const pose = space ? frame.getPose(space, referenceSpace) : null;
      if (!pose) {
        beam.group.visible = false;
        beam.dot.visible = false;
        continue;
      }

      const p = pose.transform.position;
      const o = pose.transform.orientation;
      beam.group.visible = true;
      beam.group.position.set(p.x, p.y, p.z);
      beam.group.quaternion.set(o.x, o.y, o.z, o.w);
      beam.shaft.scale.z = this.#length;
      beam.dot.visible = false;

      const origin = this.#origins[i].set(p.x, p.y, p.z);
      const direction = this.#directions[i].set(0, 0, -1).applyQuaternion(beam.group.quaternion);
      this.rays.push({ origin, direction });
      this.#rayBeam.push(i);
    }
  }

  /**
   * Rest one ray on a panel: shorten the beam to the hit and show the dot.
   * `rayIndex` indexes `rays`, so callers can pass back whatever a panel hit
   * test reported. Passing no arguments clears every hit.
   */
  setHit(rayIndex?: number, distance?: number) {
    for (const beam of this.#beams) {
      beam.dot.visible = false;
      beam.shaft.scale.z = this.#length;
    }
    if (rayIndex === undefined || distance === undefined) return;
    const beam = this.#beams[this.#rayBeam[rayIndex]];
    if (!beam) return;
    beam.shaft.scale.z = distance;
    beam.dot.position.set(0, 0, -distance);
    beam.dot.visible = true;
  }

  /** Session end / mode change: stop drawing and drop the pose state. */
  reset() {
    this.setVisible(false);
    this.rays.length = 0;
    this.#rayBeam.length = 0;
    for (const beam of this.#beams) {
      beam.group.visible = false;
      beam.dot.visible = false;
    }
  }
}
